import {
  type AnalysisOverviewItemSharing,
  AnalysisOverviewSharedWithMeStatus,
  type DetailWithCurrentUser,
  type UserProfile,
} from "Infrastructure/ApiZfp/Schema";

export type AnalysisAccessAdvisor = {
  readonly guid: string;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly fullName: string;
  readonly ssoId: string;
  readonly imageUrl: string | null;
  readonly isOwner: boolean;
  readonly isCurrent: boolean;
};

type AccessAdvisorsInput = {
  readonly sharing: AnalysisOverviewItemSharing;
  readonly agentsByGuid: ReadonlyMap<string, DetailWithCurrentUser>;
  readonly currentUser: UserProfile | null;
};

type Advisor = Omit<AnalysisAccessAdvisor, "isOwner" | "isCurrent">;

const fromAgent = (agent: DetailWithCurrentUser): Advisor => ({
  guid: agent.guid,
  firstName: agent.firstName,
  lastName: agent.lastName,
  fullName: [agent.firstName, agent.lastName].filter(Boolean).join(" ") || agent.name,
  ssoId: agent.ssoId,
  imageUrl: agent.imageUrl,
});

const fromUser = (user: UserProfile): Advisor => ({
  // the sharing refers to advisor guids, the user guid is only a fallback key
  guid: user.advisorGuid ?? user.guid,
  firstName: user.firstName,
  lastName: user.lastName,
  fullName: `${user.firstName} ${user.lastName}`,
  ssoId: user.ssoId,
  imageUrl: user.profilePictureUrl ?? null,
});

/**
 * Owner always first, then the advisor holding the edit access (when it is not the owner), then the others the
 * analysis was shared with (newest first).
 */
export const getAnalysisAccessAdvisors = ({ sharing, agentsByGuid, currentUser }: AccessAdvisorsInput): AnalysisAccessAdvisor[] => {
  const me = currentUser ? [fromUser(currentUser)] : [];

  // null means my own analysis that was never shared, so I am the owner holding the edit access
  if (!sharing) {
    return me.map((advisor) => ({ ...advisor, isOwner: true, isCurrent: true }));
  }

  const resolve = (guid: string | null): Advisor[] => {
    const agent = guid ? agentsByGuid.get(guid) : undefined;
    return agent ? [fromAgent(agent)] : [];
  };

  const isSharedWithMe = sharing.status === AnalysisOverviewSharedWithMeStatus.shared_with_me;
  const owner = isSharedWithMe ? resolve(sharing.sharedByAdvisorGuid) : me;
  const advisors = [...owner, ...[...sharing.sharedWithAdvisorGuids].reverse().flatMap(resolve)];

  const ownerGuid = owner[0]?.guid;
  // null means the owner holds the edit access
  const editorGuid = sharing.editorAdvisorGuid ?? ownerGuid;

  // the sort is stable, so the others keep their order
  const rank = (advisor: Advisor) => (advisor.guid === ownerGuid ? 0 : advisor.guid === editorGuid ? 1 : 2);

  return advisors
    .sort((a, b) => rank(a) - rank(b))
    .map((advisor) => ({ ...advisor, isOwner: advisor.guid === ownerGuid, isCurrent: advisor.guid === editorGuid }));
};
